import cn from "classnames";
import { formatDistanceToNow } from "date-fns";
import type { FC } from "react";
import type {
	InboxConversationDto,
	InboxConversationFiltersDto,
	Platform,
} from "shared";
import { Icon, IconSVG } from "../common/Icon";
import { InboxAvatar } from "./InboxAvatar";
import { useInboxConversations } from "./useInbox";
import "./InboxConversationList.css";

interface InboxConversationListProps {
	filters: InboxConversationFiltersDto;
	selectedId?: number;
	onSelect: (conversation: InboxConversationDto) => void;
}

const platformIcons: Partial<Record<Platform, keyof typeof IconSVG>> = {
	facebook: "Facebook",
	instagram: "Instagram",
	linkedin: "LinkedIn",
	threads: "BrandThreads",
	tiktok: "TikTok",
	wordpress: "Wordpress",
	"custom-blog": "Wordpress",
};

function platformBadge(platform: string) {
	const iconKey = platformIcons[platform as Platform];
	return iconKey ? <Icon name={IconSVG[iconKey]} /> : null;
}

export const InboxConversationList: FC<InboxConversationListProps> = ({
	filters,
	selectedId,
	onSelect,
}) => {
	const { data: conversations } = useInboxConversations(filters);

	if (conversations.length === 0) {
		return (
			<div className="inbox-list-empty">
				<Icon name="forum" size={48} />
				<p>No conversations yet</p>
				<small>
					{filters.archived
						? "No archived conversations"
						: "Comments and messages from your social media will appear here"}
				</small>
			</div>
		);
	}

	return (
		<div className="inbox-list">
			{conversations.map((conversation) => (
				<button
					key={conversation.id}
					type="button"
					className={cn("inbox-list-item", {
						selected: selectedId === conversation.id,
						unread: conversation.unreadCount > 0,
					})}
					onClick={() => onSelect(conversation)}
				>
					<InboxAvatar
						url={
							conversation.conversationType === "comment"
								? conversation.postThumbnailUrl ||
									conversation.participantAvatarUrl
								: conversation.participantAvatarUrl
						}
						label={
							conversation.participantName ||
							conversation.participantUsername ||
							"Post"
						}
						variant={
							conversation.conversationType === "comment" ? "post" : "person"
						}
						badge={platformBadge(conversation.platform)}
					/>

					<div className="inbox-list-item-content">
						<div className="inbox-list-item-header">
							<span className="inbox-list-item-name">
								{conversation.participantName ||
									conversation.participantUsername ||
									"Unknown"}
							</span>
							{conversation.lastMessageAt && (
								<span className="inbox-list-item-time">
									{formatDistanceToNow(new Date(conversation.lastMessageAt), {
										addSuffix: false,
									})}
								</span>
							)}
						</div>
						<div className="inbox-list-item-preview">
							<span className="inbox-list-item-type">
								{conversation.conversationType === "comment"
									? "Comment"
									: "Message"}
							</span>
							<span className="inbox-list-item-text">
								{conversation.lastMessagePreview || "No messages yet"}
							</span>
						</div>
					</div>

					{conversation.unreadCount > 0 && (
						<div className="inbox-list-item-badge">
							{conversation.unreadCount}
						</div>
					)}
				</button>
			))}
		</div>
	);
};
